import type { SafeParseError } from 'zod'
import type {
    ContractStatusResponseObj,
    PartialSuccessContractSchemaObj,
} from '../../checkout-be/src/schemas/getContractStatus'
import { createContract, pollContractStatus } from './contractService'
import { createContractPayload } from './createPayload'
import { htmlElements } from './htmlElements'
import { showErrorScreen, showSuccessScreen } from './injectHtml'
import { CheckoutHandler } from './sdk'
import { validateACHForm, validatePaymentOption } from './validation'
import type { InitOptions } from '../models/sdkModels'
import { VersionConfig } from './versionConfig'
import type { TrackingEventSchema } from '../../checkout-be/src/schemas/postEvent'
import { disableSubmit, enableSubmit } from './behaviour'

export const enum CheckoutEvent {
    SUCCESS = 'dngCheckoutSuccess',
    TIMEOUT = 'dngCheckoutTimeout',
    FAILURE = 'dngCheckoutFailure',
    PARTIAL = 'dngCheckoutPartialSuccess',
}

export const dispatchEvent = (
    eventType: CheckoutEvent,
    payload: { eventDescription: string } & (
        | {
              transactionId: string
              status: 'success'
              contracts: NonNullable<ContractStatusResponseObj['contracts']>
          }
        | {
              transactionId?: string
              status: 'error'
          }
        | {
              transactionId: string
              status: 'timeout'
          }
        | {
              status: 'zodError'
              error: SafeParseError<InitOptions>['error']['issues']
          }
        | {
              transactionId: string
              status: 'partial_success'
              contracts: NonNullable<ContractStatusResponseObj['contracts']>
              failedContracts: PartialSuccessContractSchemaObj['failedContracts']
          }
    ),
): void => {
    window.dispatchEvent(
        new CustomEvent(eventType, {
            detail: { ...payload, metadata: VersionConfig },
        }),
    )
}

export const addSubmitListener = (checkoutInstance: CheckoutHandler): void => {
    const { submitButton } = htmlElements()

    submitButton.addEventListener('click', async (): Promise<void> => {
        const paymentOptionState = await validatePaymentOption(checkoutInstance)
        checkoutInstance.formSubmitted = true

        if (
            paymentOptionState.formIsValid &&
            !checkoutInstance.creatingContract
        ) {
            // Only create contract if the form is valid and not currently attempting to create one already
            const contractPayload = createContractPayload(
                checkoutInstance,
                paymentOptionState.achFormData,
                paymentOptionState.pspToken,
            )

            const body = JSON.stringify(contractPayload)

            try {
                disableSubmit(checkoutInstance)
                checkoutInstance.transactionId = await createContract(
                    checkoutInstance.authToken,
                    body,
                )

                await handlePolling(
                    checkoutInstance,
                    checkoutInstance.transactionId,
                )
            } catch (error) {
                console.error('Error creating contract', error)
                dispatchEvent(CheckoutEvent.FAILURE, {
                    transactionId: checkoutInstance.transactionId,
                    status: 'error',
                    eventDescription: 'Failed to create contract(s)',
                })

                postEvent(checkoutInstance.authToken, {
                    eventName: CheckoutEvent.FAILURE,
                    transactionId: checkoutInstance.transactionId,
                    source: 'checkout_sdk',
                    additionalProps: {
                        status: 'error',
                        eventDescription: 'Failed to create contract(s)',
                    },
                    checkoutSourceRef:
                        checkoutInstance.basket.channelSourceData
                            .checkoutSourceRef,
                })
                showErrorScreen(checkoutInstance)
            }
        }
    })
}

export const handlePolling = async (
    checkoutInstance: CheckoutHandler,
    transactionId: string,
): Promise<void> => {
    const { dgLoader, succcessMessage } = htmlElements()

    await pollContractStatus(
        checkoutInstance.authToken,
        transactionId,
        1000,
    ).then((response) => {
        const { status, contracts } = response
        const isSuccess = status === 'success' && contracts
        const isTimeout = status === 'timeout'
        const isPartial =
            status === 'partial_success' &&
            'failedContracts' in response &&
            contracts

        if (isSuccess) {
            dgLoader.style.display = 'none'
            succcessMessage.style.display = 'block'
            dispatchEvent(CheckoutEvent.SUCCESS, {
                transactionId,
                status: 'success',
                contracts,
                eventDescription: 'Successfully created all contract(s)',
            })
            showSuccessScreen(checkoutInstance)
        } else if (isTimeout) {
            const eventDescription = 'Get contract status polling timed out'
            dispatchEvent(CheckoutEvent.TIMEOUT, {
                transactionId,
                status,
                eventDescription,
            })

            postEvent(checkoutInstance.authToken, {
                eventName: CheckoutEvent.TIMEOUT,
                transactionId,
                source: 'checkout_sdk',
                additionalProps: {
                    status,
                    eventDescription,
                },
                checkoutSourceRef:
                    checkoutInstance.basket.channelSourceData.checkoutSourceRef,
            })
        } else if (isPartial) {
            dispatchEvent(CheckoutEvent.PARTIAL, {
                transactionId,
                status: 'partial_success',
                contracts,
                failedContracts: response.failedContracts,
                eventDescription: `Successfully created ${contracts.length} contract(s), ${response.failedContracts?.length} contracts failed`,
            })
        } else {
            const eventDescription = 'Get contract status failed'
            dispatchEvent(CheckoutEvent.FAILURE, {
                transactionId,
                status: 'error',
                eventDescription,
            })

            postEvent(checkoutInstance.authToken, {
                eventName: CheckoutEvent.FAILURE,
                transactionId,
                source: 'checkout_sdk',
                additionalProps: {
                    status: 'error',
                    eventDescription,
                },
                checkoutSourceRef:
                    checkoutInstance.basket.channelSourceData.checkoutSourceRef,
            })

            showErrorScreen(checkoutInstance)
        }

        enableSubmit(checkoutInstance)
    })
}

export const addBlurListeners = (checkoutInstance: CheckoutHandler): void => {
    const { formInputs } = htmlElements()

    formInputs.forEach((element: HTMLElement) => {
        element.addEventListener('blur', () => {
            if (checkoutInstance.formSubmitted) {
                validateACHForm()
            }
        })
    })
}

export const postEvent = async (
    authToken: string,
    event: TrackingEventSchema,
): Promise<void> => {
    const body = JSON.stringify(event)
    await fetch(CheckoutHandler.postEventUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
        },
        body,
    })
}
